import React from 'react';
import { createPortal } from 'react-dom';
import { Platform, TouchableOpacity } from 'react-native';

import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';

export type MembersFilterRole = 'admin' | 'editor' | 'viewer' | 'member';

const roleLabels: Record<MembersFilterRole, string> = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
  member: 'Member',
};

type RoleFilterOptionProps<T extends MembersFilterRole> = {
  role: T;
  selectedRole: T | null;
  onRoleChange: (role: T | null) => void;
  showTopBorder?: boolean;
};

const RoleFilterOption = <T extends MembersFilterRole>({
  role,
  selectedRole,
  onRoleChange,
  showTopBorder = false,
}: RoleFilterOptionProps<T>) => {
  const handlePress = () => {
    onRoleChange(selectedRole === role ? null : role);
  };

  const isSelected = selectedRole === role;
  const roleLabel = roleLabels[role];

  return (
    <SimpleHoverable>
      {(isHovered) => (
        <Box
          backgroundColor={isHovered ? 'grey01' : 'white'}
          borderTopWidth={showTopBorder ? 1 : 0}
          borderTopColor='grey02'
          paddingHorizontal='m'
          paddingVertical='xs'>
          <RadioButton
            iconVariant='m'
            onPress={handlePress}
            isSelected={isSelected}
            text={roleLabel}
            textStyle={{
              fontSize: 14,
              marginLeft: 8,
            }}
          />
        </Box>
      )}
    </SimpleHoverable>
  );
};

type MembersFilterOverlayProps<T extends MembersFilterRole> = {
  visible: boolean;
  searchValue: string;
  roleOptions: readonly T[];
  selectedRole: T | null;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onRoleChange: (role: T | null) => void;
  onClearAll: () => void;
  overlayRef?: React.RefObject<HTMLDivElement | null>;
  outsideClickRef?: { current: HTMLDivElement | null | undefined };
  position?: { top: number; left: number };
};

export const MembersFilterOverlay = <T extends MembersFilterRole>({
  visible,
  searchValue,
  roleOptions,
  selectedRole,
  onClose: _onClose,
  onSearchChange,
  onRoleChange,
  onClearAll,
  overlayRef,
  outsideClickRef,
  position,
}: MembersFilterOverlayProps<T>) => {
  if (!visible) return null;

  const overlay = (
    <Box
      ref={(ref) => {
        if (ref && Platform.OS === 'web') {
          if (overlayRef) {
            overlayRef.current = ref as unknown as HTMLDivElement;
          }
          if (outsideClickRef) {
            outsideClickRef.current = ref as unknown as HTMLDivElement;
          }
        }
      }}
      style={{ ...position, position: 'fixed' } as any}
      zIndex={1000}>
      <ShadowBox
        width={280}
        backgroundColor='white'
        borderRadius='xs'
        variant='base'>
        <Box padding='m'>
          <SearchInput
            onTextChanged={onSearchChange}
            value={searchValue}
            placeHolder='Search members...'
            closeIconSize='s'
          />
        </Box>

        {roleOptions.map((role, index) => (
          <RoleFilterOption
            key={role}
            role={role}
            selectedRole={selectedRole}
            onRoleChange={onRoleChange}
            showTopBorder={index === 0}
          />
        ))}

        <Box
          borderTopWidth={1}
          borderTopColor='grey02'
          padding='m'
          alignItems='center'>
          <TouchableOpacity
            onPress={onClearAll}
            disabled={!searchValue && !selectedRole}>
            <Text variant='bodySecondary' color='textSecondary'>
              Clear All
            </Text>
          </TouchableOpacity>
        </Box>
      </ShadowBox>
    </Box>
  );

  if (typeof document === 'undefined' || !document.body) {
    return overlay;
  }

  return createPortal(overlay, document.body);
};
